import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { IdentityProfile } from '../../app/types';
import { fileToBase64 } from '../../lib/files';
import PageSection from '../shared/PageSection';

interface IdentityProfilesPageProps {
  readonly tenantId: string;
  readonly profiles: IdentityProfile[];
  readonly onCreateProfile: (payload: {
    tenantId: string;
    idNumber: string;
    name: string;
    surname: string;
    phoneNumber: string;
  }) => Promise<void>;
  readonly onUpdateProfile: (
    profileId: string,
    payload: { tenantId: string; idNumber?: string; name?: string; surname?: string; phoneNumber?: string }
  ) => Promise<void>;
  readonly onDeleteProfile: (profileId: string, tenantId: string) => Promise<void>;
  readonly onUploadReferencePhoto: (
    profileId: string,
    payload: { tenantId: string; sourceImageName: string; imageBase64: string }
  ) => Promise<void>;
  readonly onGetReferencePhotoUrl: (profileId: string, tenantId: string) => Promise<string>;
}

const PAGE_SIZE = 5;
const MAX_REFERENCE_FILE_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png']);
const REFERENCE_FILE_SIZE_MESSAGE =
  'Reference photo is too large. Maximum supported size is 5 MB. Please upload a smaller image.';
const REFERENCE_FILE_TYPE_MESSAGE = 'Unsupported image format. Please upload a JPEG or PNG image.';

export default function IdentityProfilesPage({
  tenantId,
  profiles,
  onCreateProfile,
  onUpdateProfile,
  onDeleteProfile,
  onUploadReferencePhoto,
  onGetReferencePhotoUrl,
}: IdentityProfilesPageProps) {
  const [idNumber, setIdNumber] = useState('');
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [editingProfileId, setEditingProfileId] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');
  const [uploadingProfileId, setUploadingProfileId] = useState('');
  const [previewProfileId, setPreviewProfileId] = useState('');
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [saving, setSaving] = useState(false);

  const filteredProfiles = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return profiles.filter((profile) => {
      if (statusFilter !== 'all' && profile.status !== statusFilter) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const haystack = [profile.name, profile.surname, profile.idNumber, profile.phoneNumber].join(' ').toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [profiles, search, statusFilter]);

  const pageCount = Math.max(1, Math.ceil(filteredProfiles.length / PAGE_SIZE));
  const visibleProfiles = filteredProfiles.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetForm = () => {
    setIdNumber('');
    setName('');
    setSurname('');
    setPhoneNumber('');
    setEditingProfileId('');
  };

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      if (editingProfileId) {
        await onUpdateProfile(editingProfileId, {
          tenantId,
          idNumber,
          name,
          surname,
          phoneNumber,
        });
      } else {
        await onCreateProfile({
          tenantId,
          idNumber,
          name,
          surname,
          phoneNumber,
        });
      }
      resetForm();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Unable to save identity profile.');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (profile: IdentityProfile) => {
    setEditingProfileId(profile.profileId);
    setIdNumber(profile.idNumber);
    setName(profile.name);
    setSurname(profile.surname);
    setPhoneNumber(profile.phoneNumber);
  };

  const removeProfile = async (profileId: string) => {
    try {
      setError('');
      await onDeleteProfile(profileId, tenantId);
      if (editingProfileId === profileId) {
        resetForm();
      }
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Unable to delete identity profile.');
    }
  };

  const uploadReference = async (profileId: string, files: FileList | null) => {
    const file = files?.[0];
    if (!file) {
      return;
    }

    if (file.size > MAX_REFERENCE_FILE_BYTES) {
      setError(REFERENCE_FILE_SIZE_MESSAGE);
      return;
    }
    if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
      setError(REFERENCE_FILE_TYPE_MESSAGE);
      return;
    }

    try {
      setUploadingProfileId(profileId);
      setError('');
      const imageBase64 = await fileToBase64(file);
      await onUploadReferencePhoto(profileId, {
        tenantId,
        sourceImageName: file.name,
        imageBase64,
      });
    } catch (uploadError) {
      const fallbackMessage = uploadError instanceof Error ? uploadError.message : 'Unable to upload reference photo.';
      if (fallbackMessage.toLowerCase().includes('payload_too_large') || fallbackMessage.toLowerCase().includes('exceeds')) {
        setError(REFERENCE_FILE_SIZE_MESSAGE);
      } else {
        setError(fallbackMessage);
      }
    } finally {
      setUploadingProfileId('');
    }
  };

  const openPreview = async (profile: IdentityProfile) => {
    try {
      setError('');
      setPreviewProfileId(profile.profileId);
      setPreviewTitle(`${profile.name} ${profile.surname} (${maskIdNumber(profile.idNumber)})`);
      const url = await onGetReferencePhotoUrl(profile.profileId, tenantId);
      setPreviewImageUrl(url);
    } catch (previewError) {
      setPreviewProfileId('');
      setPreviewImageUrl('');
      setError(previewError instanceof Error ? previewError.message : 'Unable to load reference photo preview.');
    }
  };

  const closePreview = () => {
    setPreviewProfileId('');
    setPreviewImageUrl('');
    setPreviewTitle('');
  };

  return (
    <>
      <Stack spacing={3}>
        <PageSection
          title="Identity Profiles"
          subtitle="Tenant-scoped people records used for 1:1 face verification by ID number. Reference-photo limits: JPEG/PNG up to 5 MB."
        >
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                placeholder="Name, ID number, phone"
                fullWidth
              />
              <TextField
                select
                label="Status"
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value);
                  setPage(1);
                }}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="all">All statuses</MenuItem>
                <MenuItem value="missing-reference-photo">Missing Reference</MenuItem>
                <MenuItem value="ready">Ready</MenuItem>
                <MenuItem value="invalid-reference-photo">Invalid Reference</MenuItem>
              </TextField>
            </Stack>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Identity</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleProfiles.map((profile) => (
                  <TableRow key={profile.profileId}>
                    <TableCell>
                      <strong>{profile.name} {profile.surname}</strong>
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        ID: {maskIdNumber(profile.idNumber)}
                      </Typography>
                    </TableCell>
                    <TableCell>{maskPhoneNumber(profile.phoneNumber)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={profile.status} color={profile.status === 'ready' ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          Faces: {profile.referenceFaceCount}
                        </Typography>
                        <Button
                          component="label"
                          size="small"
                          variant="outlined"
                          disabled={uploadingProfileId === profile.profileId}
                        >
                          {uploadingProfileId === profile.profileId ? 'Uploading...' : profile.referenceImageKey ? 'Replace Photo' : 'Upload Photo'}
                          <input
                            hidden
                            type="file"
                            accept="image/jpeg,image/png"
                            onChange={(event) => void uploadReference(profile.profileId, event.target.files)}
                          />
                        </Button>
                        {profile.referenceImageKey ? (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => void openPreview(profile)}
                            disabled={previewProfileId === profile.profileId && !previewImageUrl}
                          >
                            {previewProfileId === profile.profileId && !previewImageUrl ? 'Loading...' : 'View Photo'}
                          </Button>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell>{new Date(profile.createdAt).toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" variant="text" onClick={() => startEdit(profile)}>
                          Edit
                        </Button>
                        <Button size="small" color="error" variant="text" onClick={() => void removeProfile(profile.profileId)}>
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {!visibleProfiles.length ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography color="text.secondary">No identity profiles match the current filters.</Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" color="text.secondary">
                Showing {visibleProfiles.length} of {filteredProfiles.length} profiles
              </Typography>
              <Pagination page={page} count={pageCount} onChange={(_, value) => setPage(value)} size="small" />
            </Stack>
          </Stack>
        </PageSection>

        <PageSection
          title={editingProfileId ? 'Edit Identity Profile' : 'Create Identity Profile'}
          subtitle="Register a person before uploading a reference face image."
        >
          <Stack spacing={2}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <TextField label="ID Number" value={idNumber} onChange={(event) => setIdNumber(event.target.value)} />
            <TextField label="Name" value={name} onChange={(event) => setName(event.target.value)} />
            <TextField label="Surname" value={surname} onChange={(event) => setSurname(event.target.value)} />
            <TextField label="Phone Number" value={phoneNumber} onChange={(event) => setPhoneNumber(event.target.value)} />
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={() => void submit()}
                disabled={!idNumber || !name || !surname || !phoneNumber || saving}
              >
                {saving ? 'Saving...' : editingProfileId ? 'Save Changes' : 'Create Profile'}
              </Button>
              {editingProfileId ? (
                <Button variant="outlined" onClick={resetForm} disabled={saving}>
                  Cancel
                </Button>
              ) : null}
            </Stack>
          </Stack>
        </PageSection>
      </Stack>

      <Dialog open={Boolean(previewProfileId)} onClose={closePreview} maxWidth="md" fullWidth>
        <DialogTitle>{previewTitle || 'Reference Photo'}</DialogTitle>
        <DialogContent>
          {previewImageUrl ? (
            <img
              src={previewImageUrl}
              alt="Reference profile"
              style={{ width: '100%', height: 'auto', borderRadius: 8, display: 'block' }}
            />
          ) : (
            <Typography color="text.secondary">Loading image...</Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function maskIdNumber(value: string): string {
  if (value.length <= 4) {
    return value;
  }

  return `${'*'.repeat(Math.max(0, value.length - 4))}${value.slice(-4)}`;
}

function maskPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 4) {
    return value;
  }

  return `${'*'.repeat(Math.max(0, digits.length - 4))}${digits.slice(-4)}`;
}
